import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserRegistration,
  onUserSignUpByStep,
  getAvailableLanguages,
  getInvitationCode,
} from "actions/signup";
import { tokenStorage } from "lib/magiclinks-api/token-storage";
import MovileNav from "./MobileNav";
import { ChevronLeftIcon } from "lucide-react";

// Form Steps
import StepInitialData from "./FormSteps/InitialData";
import ConfirmCode from "./FormSteps/ConfirmCode";
import StepPassword from "./FormSteps/SetPassword";
import NameAndBirth from "./FormSteps/NameAndBirth";
import CommunicationPreferences from "./FormSteps/CommunicationPreferences";
import ConnectSocials from "./FormSteps/ConnectSocials";
import AgencyManager from "./FormSteps/AgencyManager";
import AffiliateReferral from "./FormSteps/AffiliateReferral";
import Review from "./Review/Review";
import ReviewFinishSignUp from "./Review/ReviewFinishSignUp";
import LoadingSpinner from "views/partials/LoadingSpinner";

const totalSteps = 9;

export default function Signup() {
  const [formData, setFormData] = React.useState();
  const [currentStep, setCurrentStep] = React.useState(1);
  const [_, setShouldLoad] = React.useState(false);
  const dispatch = useDispatch();

  const signupToken = useSelector((state) => state.signup.signupToken || null);
  const rewardsUser = useSelector((state) => state.signup.rewardsUser || null);
  const languagesSelector = useSelector(
    (state) => state.signup.languages || []
  );
  const isEmailUsed = useSelector((state) => state.signup.isEmailUsed || false);
  const fetchingUserRegistration = useSelector(
    (state) => state.signup.fetchingUserRegistration || false
  );
  const rewardsUserInvitation = useSelector(
    (state) => state.signup.rewardsUserInvitation || null
  );

  const queryParams = new URLSearchParams(window.location.search);
  const invitationCode = queryParams.get("invitation_code");

  React.useEffect(() => {
    dispatch(getAvailableLanguages());
  }, []);

  React.useEffect(() => {
    if (fetchingUserRegistration) return;
    if (!rewardsUser) return;
    setFormData(rewardsUser);
  }, [fetchingUserRegistration, rewardsUser]);

  React.useEffect(() => {
    if (!tokenStorage.getToken()) {
      setShouldLoad(true);
      return;
    }
    dispatch(
      getUserRegistration((step) => {
        setShouldLoad(true);
        setCurrentStep(step - 1);
      })
    );
  }, []);

  React.useEffect(() => {
    if (invitationCode) dispatch(getInvitationCode(invitationCode));
  }, []);

  React.useEffect(() => {
    if (rewardsUserInvitation?.email) {
      setFormData({ ...formData, email: rewardsUserInvitation.email });
    }
  }, [rewardsUserInvitation]);

  const signupRequest = () => {
    handleSignUpByStep(
      {
        rewards_user: {
          password: formData.password,
          password_confirmation: formData.password_confirmation,
          submit: true,
        },
      },
      "registrations"
    );
  };

  const handleSignUpByStep = (data, action, callback) => {
    dispatch(onUserSignUpByStep(data, action, callback));
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen bg-gray-50 font-sans">
        {rewardsUser?.application_date ? (
          <ReviewFinishSignUp
            name={formData.first_name}
            rewardsUserInvitation={rewardsUserInvitation}
          />
        ) : currentStep >= totalSteps ? (
          <Review
            formData={formData}
            onChange={setFormData}
            prevStep={() => setCurrentStep(8)}
            nextStep={signupRequest}
            goToStep={(step) => setCurrentStep(step)}
          />
        ) : (
          <>
            <div className="flex flex-1 flex-col justify-start py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24 bg-gray-50 relative">
              <a
                href="/rewards_users/login"
                className="border-none bg-none appearance-none py-1 hidden md:flex items-center justify-start group hover:no-underline mb-10"
              >
                <span className="inline-flex items-center justify-center rounded-full bg-white border border-gray-300 p-2 mr-2 group-hover:bg-gray-100 group-hover:border-gray-200">
                  <ChevronLeftIcon className="h-4 w-4 text-primary-700 group-hover:text-primary-500" />
                </span>
                <span className="text-sm text-primary-700 group-hover:text-primary-500">
                  Back to log in
                </span>
              </a>
              <MovileNav />
              {!rewardsUser.first_name && fetchingUserRegistration ? (
                <div className="mx-auto w-full lg:w-[480px]">
                  <div className="mt-8">
                    <div className="mb-4 relative w-full">
                      <LoadingSpinner
                        size="spinner-medium align-center"
                        isActive={true}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {currentStep === 1 && (
                    <StepInitialData
                      formData={formData}
                      onChange={setFormData}
                      nextStep={() => setCurrentStep(2)}
                      currentStep={currentStep}
                      totalSteps={totalSteps}
                      isEmailUsed={isEmailUsed}
                      rewardsUserInvitation={rewardsUserInvitation}
                      onUserSignUpByStep={(callback) =>
                        handleSignUpByStep(
                          {
                            rewards_user: {
                              first_name: formData.first_name,
                              last_name: formData.last_name,
                              email: formData.email,
                            },
                          },
                          "send_code",
                          callback
                        )
                      }
                    />
                  )}
                  {currentStep === 2 && (
                    <ConfirmCode
                      formData={formData}
                      onChange={setFormData}
                      prevStep={() => setCurrentStep(1)}
                      nextStep={() => setCurrentStep(3)}
                      currentStep={currentStep}
                      totalSteps={totalSteps}
                      onUserSignUpByStep={(verificationCode, callback) => {
                        handleSignUpByStep(
                          verificationCode,
                          "confirm_code",
                          callback
                        );
                      }}
                      onResendCode={() => {
                        handleSignUpByStep(
                          {
                            rewards_user: {
                              first_name: formData.first_name,
                              last_name: formData.last_name,
                              email: formData.email,
                            },
                          },
                          "send_code"
                        );
                      }}
                    />
                  )}
                  {currentStep === 3 && (
                    <StepPassword
                      formData={formData}
                      onChange={setFormData}
                      prevStep={() => setCurrentStep(2)}
                      nextStep={() => setCurrentStep(4)}
                      onUserSignUpByStep={(callback) => {
                        if (rewardsUser && rewardsUser.application_start_date) {
                          handleSignUpByStep(
                            {
                              rewards_user: {
                                password: formData.password,
                                password_confirmation:
                                  formData.password_confirmation,
                              },
                            },
                            "registrations",
                            callback()
                          );
                        } else {
                          handleSignUpByStep(
                            {
                              signup_token: signupToken,
                              invitation_code: invitationCode,
                              rewards_user: {
                                first_name: formData.first_name,
                                last_name: formData.last_name,
                                email: formData.email,
                                password: formData.password,
                                password_confirmation:
                                  formData.password_confirmation,
                              },
                            },
                            "registrations",
                            callback()
                          );
                        }
                      }}
                      currentStep={currentStep}
                      totalSteps={totalSteps}
                    />
                  )}
                  {currentStep === 4 && (
                    <NameAndBirth
                      formData={formData}
                      onChange={setFormData}
                      rewardsUser={rewardsUser}
                      onUserSignUpByStep={(callback) => {
                        handleSignUpByStep(
                          {
                            rewards_user: {
                              display_name: formData.display_name,
                              birthday: formData.birthday,
                            },
                          },
                          "registrations",
                          callback()
                        );
                      }}
                      prevStep={() => setCurrentStep(3)}
                      nextStep={() => setCurrentStep(5)}
                      currentStep={currentStep}
                      totalSteps={totalSteps}
                    />
                  )}
                  {currentStep === 5 && (
                    <CommunicationPreferences
                      formData={formData}
                      onChange={setFormData}
                      onUserSignUpByStep={(callback) => {
                        handleSignUpByStep(
                          {
                            rewards_user: {
                              phone: formData.phone,
                              sms_opt_in: formData.sms_opt_in,
                              country: formData.country,
                              language: formData.language,
                            },
                          },
                          "registrations",
                          callback()
                        );
                      }}
                      languages={languagesSelector}
                      prevStep={() => setCurrentStep(4)}
                      nextStep={() => setCurrentStep(6)}
                      currentStep={currentStep}
                      totalSteps={totalSteps}
                    />
                  )}
                  {currentStep === 6 && (
                    <ConnectSocials
                      formData={formData}
                      onChange={setFormData}
                      onUserSignUpByStep={(callback) => {
                        handleSignUpByStep(
                          {
                            rewards_user: {
                              update_social_channels: true,
                            },
                          },
                          "registrations",
                          callback()
                        );
                      }}
                      prevStep={() => setCurrentStep(5)}
                      nextStep={() => setCurrentStep(7)}
                      currentStep={currentStep}
                      totalSteps={totalSteps}
                    />
                  )}
                  {currentStep === 7 && (
                    <AgencyManager
                      formData={formData}
                      onChange={setFormData}
                      onUserSignUpByStep={(callback) => {
                        handleSignUpByStep(
                          {
                            rewards_user: {
                              manager_represented: formData.manager_represented,
                              manager_name: formData.manager_name,
                              manager_email: formData.manager_email,
                              manager_reach_out: formData.manager_reach_out,
                            },
                          },
                          "registrations",
                          callback()
                        );
                      }}
                      prevStep={() => setCurrentStep(6)}
                      nextStep={() => setCurrentStep(8)}
                      currentStep={currentStep}
                      totalSteps={totalSteps}
                    />
                  )}
                  {currentStep === 8 && (
                    <AffiliateReferral
                      formData={formData}
                      onChange={setFormData}
                      onUserSignUpByStep={(callback) => {
                        handleSignUpByStep(
                          {
                            rewards_user: {
                              used_affiliate_before:
                                formData.used_affiliate_before,
                              rewards_hear_about_us_id:
                                formData.rewards_hear_about_us_id,
                            },
                          },
                          "registrations",
                          callback()
                        );
                      }}
                      prevStep={() => setCurrentStep(7)}
                      nextStep={() => setCurrentStep(9)}
                      currentStep={currentStep}
                      totalSteps={totalSteps}
                    />
                  )}
                  {/* {currentStep === 9 && (
                    <Terms
                      formData={formData}
                      onChange={setFormData}
                      onUserSignUpByStep={(callback) => {
                        handleSignUpByStep(
                          {
                            rewards_user: {
                              terms_opt_in: formData.terms_opt_in,
                              marketing_opt_in: formData.marketing_opt_in,
                            },
                          },
                          "registrations",
                          callback()
                        );
                      }}
                      prevStep={() => setCurrentStep(8)}
                      nextStep={() => setCurrentStep(10)}
                      currentStep={currentStep}
                      totalSteps={totalSteps}
                    />
                  )} */}
                </>
              )}
            </div>
            <div
              className="relative hidden flex-1 lg:block bg-cover bg-no-repeat bg-center"
              style={{
                backgroundImage:
                  "url('https://asset.magiclinks.com/assets/images/creators-portal/bg-signup.png')",
              }}
            ></div>
          </>
        )}
      </div>
    </>
  );
}
